import { parseReport, serializeCommand } from './protocol.js';

// Owns the LabsLand iframe bridge. Commands are serialized one at a time and a
// retry reuses its request ID, allowing firmware to acknowledge it idempotently.
function defaultRequestSeed() {
  const seconds = Math.floor(Date.now() / 1000) >>> 0;
  return seconds === 0 ? 1 : seconds;
}

export class WindStationTransport {
  constructor({
    onState = () => {},
    onConnection = () => {},
    onPending = () => {},
    send,
    notifyLoaded,
    requestSeed = defaultRequestSeed(),
    retryMs = 1000,
    disconnectMs = 1800,
    timers = window,
  } = {}) {
    this.onState = onState;
    this.onConnection = onConnection;
    this.onPending = onPending;
    this.retryMs = retryMs;
    this.disconnectMs = disconnectMs;
    this.timers = timers;
    this.nextRequestId = requestSeed;
    this.pending = null;
    this.lastSeq = 0;
    this.lastReportAt = 0;
    this.startedAt = 0;
    this.retryTimer = null;
    this.watchdogTimer = null;
    this.started = false;

    this.send = send ?? ((value) => {
      window.parent.postMessage({ messageType: 'web2sim', version: '1.0', value }, '*');
    });
    this.notifyLoaded = notifyLoaded ?? (() => {
      window.parent.postMessage({ messageType: 'web2loader', version: '1.0' }, '*');
    });
    this.handleWindowMessage = this.handleWindowMessage.bind(this);
  }

  start() {
    if (this.started) return;
    this.started = true;
    this.lastSeq = 0;
    this.lastReportAt = 0;
    this.startedAt = Date.now();
    window.addEventListener('message', this.handleWindowMessage);
    this.notifyLoaded();
    this.onConnection('connecting');
    this.watchdogTimer = this.timers.setInterval(() => this.checkConnection(), 400);
    this.requestSync();
  }

  stop() {
    if (!this.started) return;
    this.started = false;
    window.removeEventListener('message', this.handleWindowMessage);
    this.clearRetry();
    if (this.watchdogTimer !== null) this.timers.clearInterval(this.watchdogTimer);
    this.watchdogTimer = null;
    this.startedAt = 0;
    this.pending = null;
    this.onPending(false);
  }

  requestSync() { return this.queueRequest('sync'); }
  requestRestart() { return this.queueRequest('restart'); }
  requestScenario(name) { return this.queueRequest('scenario', name); }

  queueRequest(action, name) {
    if (!this.started || this.pending !== null) return false;
    const id = this.takeRequestId();
    const message = serializeCommand(id, action, name);
    this.pending = { id, message };
    this.onPending(true);
    this.send(message);
    this.scheduleRetry();
    return true;
  }

  takeRequestId() {
    const id = this.nextRequestId;
    this.nextRequestId = id >= 0xffffffff ? 1 : id + 1;
    return id;
  }

  scheduleRetry() {
    this.clearRetry();
    this.retryTimer = this.timers.setTimeout(() => {
      if (this.pending === null) return;
      this.send(this.pending.message);
      this.scheduleRetry();
    }, this.retryMs);
  }

  clearRetry() {
    if (this.retryTimer !== null) this.timers.clearTimeout(this.retryTimer);
    this.retryTimer = null;
  }

  checkConnection() {
    const referenceTime = this.lastReportAt || this.startedAt;
    if (referenceTime === 0 || Date.now() - referenceTime <= this.disconnectMs) return;
    this.onConnection('disconnected');
    if (this.pending === null) this.requestSync();
  }

  handleWindowMessage(event) {
    if (!event?.data || event.data.messageType !== 'sim2web') return;
    // Only the embedding lab may supply controller reports. A caller-provided
    // debug marker is not proof of who sent a cross-window message.
    if (event.source !== window.parent) return;
    this.receiveReport(event.data.value);
  }

  receiveReport(raw) {
    if (!this.started) return false;
    let state;
    try {
      state = parseReport(raw);
    } catch (error) {
      console.warn('[airport-wind] Ignoring invalid report:', error.message);
      return false;
    }

    // Heartbeats contain complete state too; sequence order rejects stale frames.
    if (state.seq <= this.lastSeq) return false;
    this.lastSeq = state.seq;
    this.lastReportAt = Date.now();
    this.onConnection('connected');

    if (this.pending !== null && state.activeRequestId === this.pending.id) {
      this.pending = null;
      this.clearRetry();
      this.onPending(false);
    }

    this.onState(state);
    return true;
  }
}
